import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Card, CardBody, Alert, InputGroupAddon } from 'reactstrap';
import { Form } from '@availity/form';
import { Input, Switch, Date } from '../../lib/form-field';
import formValidation from './formValidation';
import {
  createDiscountCode,
  updateDiscountCode,
  resetMessage,
} from '../../store/discountCodes/actions';
import {
  selectDiscountCodeError,
  selectDiscountCodeMessage,
} from '../../selectors/discountCode';
import { SubmitButton } from '../Common/Modal/modalActionButtons';
import moment from 'moment';

const DiscountCodeForm = ({ discountCode, onSave, loading, create }) => {
  const editingDiscountCode = discountCode || {};

  const dispatch = useDispatch();
  const generateDiscountCode = useCallback(() => {
    const allowedChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';

    const length = Math.floor(Math.random() * 2) + 6; // 6-7 characters

    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * allowedChars.length);
      result += allowedChars[randomIndex];
    }

    return result;
  }, []);

  const error = useSelector(selectDiscountCodeError);
  const message = useSelector(selectDiscountCodeMessage);

  useEffect(() => {
    if (message) {
      onSave();
    }
  }, [message, onSave]);

  useEffect(() => {
    return () => {
      dispatch(resetMessage());
    };
  }, [dispatch]);

  const handleSubmit = (values) => {
    const payload = {
      code: values.code.toUpperCase(),
      description: values.description,
      discountPercentage: parseFloat(values.discountPercentage),
      disableAutoRenewal: values.disableAutoRenewal,
      maxUses: values.maxUses ? parseInt(values.maxUses) : null,
      expirationDate: values.expirationDate || null,
      isActive: values.isActive,
    };

    if (discountCode) {
      dispatch(updateDiscountCode(discountCode.id, payload));
    } else {
      dispatch(createDiscountCode(payload));
    }
  };

  const initialValues = {
    code: editingDiscountCode.code || '',
    description: editingDiscountCode.description || '',
    discountPercentage: editingDiscountCode.discountPercentage ?? '',
    disableAutoRenewal: editingDiscountCode.disableAutoRenewal || false,
    maxUses: editingDiscountCode.maxUses || '',
    expirationDate: editingDiscountCode.expirationDate
      ? moment(editingDiscountCode.expirationDate).format('YYYY-MM-DD')
      : '',
    isActive:
      editingDiscountCode.isActive !== undefined
        ? editingDiscountCode.isActive
        : true,
  };

  return (
    <Card>
      <CardBody>
        {error && (
          <Alert color="danger">
            <i className="bx bx-error-circle mr-2"></i>
            {error}
          </Alert>
        )}

        <Form
          initialValues={initialValues}
          validationSchema={formValidation()}
          onSubmit={handleSubmit}>
          {({ isValid, setFieldValue }) => (
            <>
              <Row>
                <Col md="6">
                  <Input
                    type="text"
                    name="code"
                    id="code"
                    label="Codice Sconto"
                    placeholder="es. SCONTO50"
                    style={{ textTransform: 'uppercase' }}
                    addonType="append">
                    <InputGroupAddon addonType="append">
                      <button
                        className="btn btn-primary p-0"
                        style={{ width: '31px' }}
                        type="button"
                        onClick={() =>
                          setFieldValue('code', generateDiscountCode())
                        }>
                        <i className="bx bxs-coupon d-block font-size-15" />
                      </button>
                    </InputGroupAddon>
                  </Input>
                </Col>
                <Col md="6">
                  <Input
                    type="text"
                    name="description"
                    id="description"
                    label="Descrizione"
                    placeholder="Descrizione del codice sconto"
                  />
                </Col>
              </Row>

              <Row>
                <Col md="12">
                  <Input
                    type="number"
                    name="discountPercentage"
                    id="discountPercentage"
                    label="Percentuale di Sconto (%)"
                    placeholder="20"
                    min="0"
                    max="100"
                    helpText="Inserisci la percentuale di sconto (0-100). Usa 100 per rendere il piano gratuito."
                  />
                </Col>
              </Row>

              <Row>
                <Col md="6">
                  <Input
                    type="number"
                    name="maxUses"
                    id="maxUses"
                    label="Numero Massimo Utilizzi"
                    placeholder="Lascia vuoto per utilizzi illimitati"
                    min="1"
                  />
                </Col>
                <Col md="6">
                  <Date
                    name="expirationDate"
                    id="expirationDate"
                    label="Data Scadenza"
                    min={moment().format('YYYY-MM-DD')}
                    helpText="Lascia vuoto per nessuna scadenza"
                  />
                </Col>
              </Row>

              <Row>
                <Col md="6">
                  <Switch
                    name="disableAutoRenewal"
                    label="Disabilita rinnovo automatico"
                    helpText="Se selezionato, le sottoscrizioni create con questo codice non si rinnoveranno automaticamente"
                  />
                </Col>
                <Col md="6">
                  <Switch
                    name="isActive"
                    label="Codice attivo"
                    helpText="Solo i codici attivi possono essere utilizzati dagli utenti"
                  />
                </Col>
              </Row>

              <div className="modal-footer">
                <SubmitButton disabled={!isValid || loading} create={create} />
              </div>
            </>
          )}
        </Form>
      </CardBody>
    </Card>
  );
};

export default DiscountCodeForm;
